/**
 * Protocols we are willing to hand to the browser when navigating on behalf of
 * a URL that came from somewhere other than a literal in our own source -- an
 * API response, a document's contents, a search result. Everything else is
 * refused, most importantly `javascript:`, which runs script in our origin
 * when passed to `window.open` or assigned to `location.href`.
 */
const NAVIGABLE_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:']);

/**
 * Resolves `url` against the current document and returns it only when it is
 * safe to navigate to, or `undefined` when the input doesn't parse or uses a
 * protocol outside the allowlist. Relative URLs resolve against the current
 * origin and are therefore always allowed.
 */
export const safeNavigableUrl = (url: string): string | undefined => {
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.href);
  } catch {
    return undefined;
  }

  return NAVIGABLE_PROTOCOLS.has(parsed.protocol) ? parsed.href : undefined;
};

/**
 * `window.open` for URLs we didn't author, refusing anything that isn't plain
 * http(s). Returns the opened window when the browser hands one back, or
 * `null` when the URL was rejected or the browser blocked the popup -- callers
 * already have to handle `null` for the blocked case.
 */
export const openSafeUrl = (
  url: string,
  target?: string,
  features?: string
): Window | null => {
  const safe = safeNavigableUrl(url);
  if (safe === undefined) {
    return null;
  }

  return window.open(safe, target, features);
};
